import { useCallback, useEffect, useRef, useState } from "react";
import type { Me, PetRecord, VerifyResult } from "../../../shared/types";
import { api } from "../../api";
import { navigate } from "../../router";
import { fmtDate, lbToKg, speciesEmoji } from "../../format";
import { ChainRail } from "./ChainRail";
import { TranslateBar, useRecordTranslation } from "./Translate";
import { NewVisitModal } from "./NewVisitModal";
import { TamperModal } from "./TamperModal";
import { BehaviourTab, LabsTab, OverviewTab, OwnerTab, RxTab, VaccinesTab, VisitsTab } from "./tabs";

export const TABS = [
  ["overview", "Overview"],
  ["visits", "Visit History"],
  ["labs", "Lab Work & Images"],
  ["behaviour", "Behavior"],
  ["rx", "Prescriptions"],
  ["vaccines", "Vaccines"],
  ["owner", "Owner & ID"],
] as const;
export type TabId = (typeof TABS)[number][0];

export function RecordView({ me, petId, initialTab }: { me: Me; petId: number; initialTab?: string }) {
  const [record, setRecord] = useState<PetRecord | null>(null);
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>(TABS.some(([id]) => id === initialTab) ? (initialTab as TabId) : "overview");
  const [openVisit, setOpenVisit] = useState<number | null>(null);
  const [modal, setModal] = useState<"visit" | "tamper" | null>(null);
  const [brief, setBrief] = useState<{ loading: boolean; html?: string; error?: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const translation = useRecordTranslation(contentRef, tab);

  const load = useCallback(async () => {
    try {
      const [r, v] = await Promise.all([api.record(petId), api.verify(petId)]);
      setRecord(r);
      setVerify(v);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [petId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const switchTab = (id: TabId) => {
    setTab(id);
    navigate(`/pets/${petId}${id === "overview" ? "" : `?tab=${id}`}`, true);
  };

  const jumpToVisit = (visitId: number) => {
    setOpenVisit(visitId);
    switchTab("visits");
    requestAnimationFrame(() => document.getElementById(`visit-${visitId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }));
  };

  async function runBrief() {
    setBrief({ loading: true });
    try {
      setBrief({ loading: false, html: (await api.brief(petId)).html });
    } catch (e) {
      setBrief({ loading: false, error: (e as Error).message });
    }
  }

  if (error) return <main className="container"><div className="err">{error}</div><p style={{ marginTop: 12 }}><button className="btn ghost" onClick={() => navigate("/")}>← Back to patients</button></p></main>;
  if (!record) return <div className="center"><div className="spinner" />Loading record…</div>;

  const { pet } = record;
  const subtitle = [pet.species, pet.breed, pet.sex, pet.ageLabel, pet.weightLb ? `${pet.weightLb} lb` : null].filter(Boolean).join(" · ");
  const isVet = me.role === "vet";

  return (
    <div className="page">
      <ChainRail
        chain={record.chain}
        verify={verify}
        isVet={isVet}
        onOpen={(b) => (b.type === "visit" ? jumpToVisit(b.refId) : switchTab(b.type === "note" ? "behaviour" : "owner"))}
        onVerify={async () => { setVerify(null); setVerify(await api.verify(petId)); }}
        onTamper={() => setModal("tamper")}
      />
      <div className="main-panel">
        <div className="top-bar">
          {pet.photoUrl ? <img className="pat-photo" src={pet.photoUrl} alt={pet.name} /> : <div className="pat-photo">{speciesEmoji(pet.species)}</div>}
          <div>
            <div className="pn">{pet.displayName}</div>
            <div className="ps">{subtitle}</div>
            <div className="pid">Microchip: {pet.microchip}</div>
          </div>
          <div className="top-actions">
            <div className="top-meta">
              <div className="a">Patient ID: {pet.patientNo}</div>
              <div className="b">Record: {fmtDate(record.recordDate)}</div>
            </div>
            <button className="btn sm" style={{ background: "rgba(255,255,255,.12)" }} onClick={runBrief} disabled={brief?.loading} title="AI-generated emergency brief">⚡ Brief</button>
            {isVet && <button className="btn teal sm" onClick={() => setModal("visit")}>+ New visit</button>}
          </div>
        </div>
        <div className="tabs" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} className={`tb${tab === id ? " active" : ""}`} onClick={() => switchTab(id)}>{label}</button>
          ))}
        </div>
        <TranslateBar t={translation} />

        <div className="pn-wrap" ref={contentRef}>
          {brief && (
            <div className="brief" data-notranslate>
              <div className="brief-h"><span>⚡ Emergency brief · AI-generated from the verified record</span><button className="x" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => setBrief(null)}>×</button></div>
              {brief.loading ? "Generating brief…" : brief.error ? <span style={{ color: "#ff9d9d" }}>{brief.error}</span> : <div dangerouslySetInnerHTML={{ __html: brief.html! }} />}
            </div>
          )}
          {tab === "overview" && <OverviewTab r={record} goTo={switchTab} />}
          {tab === "visits" && <VisitsTab r={record} open={openVisit} setOpen={setOpenVisit} />}
          {tab === "labs" && <LabsTab r={record} />}
          {tab === "behaviour" && <BehaviourTab r={record} me={me} onSaved={(s) => { setToast(`Note sealed as block #${s.seq}`); load(); }} />}
          {tab === "rx" && <RxTab r={record} />}
          {tab === "vaccines" && <VaccinesTab r={record} />}
          {tab === "owner" && <OwnerTab r={record} kg={pet.weightLb ? lbToKg(pet.weightLb) : null} />}
        </div>
      </div>

      {modal === "visit" && (
        <NewVisitModal
          record={record}
          onClose={() => setModal(null)}
          onSaved={(s) => { setModal(null); setToast(`Visit sealed as block #${s.seq}${s.notes?.length ? ` + ${s.notes.length} behavior note${s.notes.length > 1 ? "s" : ""}` : ""} · ${s.hash.slice(0, 12)}…`); load(); }}
        />
      )}
      {modal === "tamper" && <TamperModal petId={petId} petName={pet.name} onClose={() => setModal(null)} />}
      {toast && <div className="toast">🔗 {toast}</div>}
    </div>
  );
}
